import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Order,
  OrdersService,
  Region,
} from '@compumark/order-management-client';
import { cloneDeep } from 'lodash';

import { UpdateOrderModel } from '../models/update-order-model';

const ADD = 'add';
const REMOVE = 'remove';
const WORLDWIDE = 'Worldwide';
const LIMIT_OF_INDIVIDUAL_REGIONS = 3;

@Component({
  selector: 'app-watched-register',
  templateUrl: './watched-register.component.html',
  styleUrls: ['./watched-register.component.scss'],
  standalone: false,
})
export class WatchedRegisterComponent implements AfterViewInit {
  orderService = inject(OrdersService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  data = inject<{
    model: UpdateOrderModel;
  }>(MAT_DIALOG_DATA);

  public order: Order;
  public search = '';
  public registerKey!: string;
  public allSelected = false;
  public watchRegisters!: {
    [key: string]: Array<string>;
  };
  selectedOptions: Set<string> = new Set<string>();
  public filteredRegisters: string[] = [];
  private registers: string[] = [];
  public region!: string[];
  public action!: string;
  private readonly originalSelectedOptions: Set<string> = new Set<string>();
  private readonly originalRegions: Region[] = [];
  private readonly productsWithRegionalCoverage: number[] = [
    1501, 1503, 1505, 1506, 1537, 1521,
  ];
  private viewInitialized = false;

  constructor() {
    this.order = this.data.model.order!;
    this.orderService
      .registerAndDomain(this.order.id!)
      .subscribe((response) => {
        this.region = Object.keys(response.allRegion!);
        this.watchRegisters = response.allRegion!;
        this.selectedOptions = new Set<string>(response.selectedOptions);
        this.selectedOptions.forEach((item) => {
          this.originalSelectedOptions.add(item);
        });
        this.order.regions
          ?.map((region) => cloneDeep(region))
          .forEach((r) => {
            this.originalRegions.push(r);
          });
        this.loadRegisters(this.region[0]);
        if (this.viewInitialized) {
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
  }

  loadRegisters(registerName: string): void {
    if (registerName) {
      this.registerKey = registerName!;
      this.filteredRegisters = this.watchRegisters[registerName!];
      this.registers = this.filteredRegisters;
      this.checkSelectAll(this.filteredRegisters, this.selectedOptions);
    }
  }

  getCount(key: string): number {
    let count = 0;
    const regis = this.watchRegisters[key];
    regis?.forEach((item: string) => {
      if (this.selectedOptions.has(item)) {
        count++;
      }
    });
    return count;
  }

  private getSelected = (key: string) => {
    return this.watchRegisters[key].filter((item) =>
      this.selectedOptions.has(item),
    );
  };

  checkSelectAll = (registers: string[], selectedOptions: Set<string>) => {
    this.allSelected = registers.every((item) => selectedOptions.has(item));
  };

  private checkNothingSelected = () => {
    return (
      this.selectedOptions.size === this.originalSelectedOptions.size &&
      Array.from(this.selectedOptions).every((item) =>
        this.originalSelectedOptions.has(item),
      )
    );
  };

  private calculateAddedAndRemovedRegisters(): {
    added: string[];
    removed: string[];
  } {
    const added = new Set<string>();
    const removed = new Set<string>();
    this.region.forEach((key) => {
      this.watchRegisters[key].forEach((r) => {
        if (
          this.selectedOptions.has(r) &&
          !this.originalSelectedOptions.has(r)
        ) {
          added.add(r);
        }
        if (
          this.originalSelectedOptions.has(r) &&
          !this.selectedOptions.has(r)
        ) {
          removed.add(r);
        }
      });
    });
    return { added: Array.from(added), removed: Array.from(removed) };
  }

  private addAddedAndRemovedRegistersToOrderModel(): void {
    const change = this.calculateAddedAndRemovedRegisters();
    this.addAddedRegistersToOrderModel(change.added);
    this.addRemovedRegistersToOrderModel(change.removed);
  }

  private addAddedRegistersToOrderModel(added: any): void {
    this.order.addedRegisters = added;
  }

  private addRemovedRegistersToOrderModel(removed: any): void {
    this.order.removedRegisters = removed;
  }

  private clearAddedRegisters(): void {
    this.order.addedRegisters = [];
  }

  private clearRemovedRegisters(): void {
    this.order.removedRegisters = [];
  }

  private loadOldRegistersIfNoChange(): void {
    if (this.checkNothingSelected()) {
      this.order.regions = [];
      this.originalRegions.forEach((region) => {
        this.order.regions?.push(region);
      });
      this.clearRemovedRegisters();
      this.clearAddedRegisters();
    }
  }

  toggleSelectAllOptions = () => {
    this.clearAddedRegisters();
    this.clearRemovedRegisters();
    if (this.productsWithRegionalCoverage.includes(this.order.productId!)) {
      if (this.filteredRegisters.length === this.registers.length) {
        if (this.allSelected) {
          this.selectAllOptions();
        } else {
          this.deselectAllOptions();
        }
      } else {
        this.filteredRegisters.forEach((register) => {
          this.toggleSelectOptions(register);
        });
      }
      this.loadOldRegistersIfNoChange();
    } else {
      if (this.allSelected) {
        this.filteredRegisters.forEach((register) => {
          this.selectedOptions.add(register);
        });
      } else {
        this.filteredRegisters.forEach((register) => {
          this.selectedOptions.delete(register);
        });
      }
      this.order.regions = Array.from(this.selectedOptions).map((label) => {
        return {
          label,
        } as Region;
      });
    }
  };

  private selectAllOptions(): void {
    this.filteredRegisters.forEach((register) => {
      this.selectedOptions.add(register);
    });
    this.addRegion();
    this.modifyRegions();
  }

  private deselectAllOptions(): void {
    this.filteredRegisters.forEach((register) => {
      this.selectedOptions.delete(register);
    });
    this.removeRegion();
    this.modifyRegions();
  }

  toggleSelectOptions = (register: string) => {
    this.clearAddedRegisters();
    this.clearRemovedRegisters();

    if (this.selectedOptions.has(register)) {
      this.allSelected = false;
      this.selectedOptions.delete(register);
      this.action = REMOVE;
    } else {
      this.selectedOptions.add(register);
      this.checkSelectAll(this.filteredRegisters, this.selectedOptions);
      this.action = ADD;
    }

    if (this.productsWithRegionalCoverage.includes(this.order.productId!)) {
      if (this.registers.every((item) => this.selectedOptions.has(item))) {
        this.selectAllOptions();
      } else {
        this.changeOrderModel(register);
        this.addAddedAndRemovedRegistersToOrderModel();
        this.modifyRegions();
        this.loadOldRegistersIfNoChange();
      }
    } else {
      this.changeOrderModelForNonRegionalProducts(register);
    }
  };

  private addRegion(): void {
    const index = this.order.regions?.findIndex(
      (region) => region.label === this.registerKey,
      0,
    );
    if (index === -1 || index === undefined) {
      this.order.regions?.push({
        label: this.registerKey,
        limited: false,
      } as Region);
    }
  }
  private removeRegion(): void {
    const index = this.order.regions?.findIndex(
      (region) => region.label === this.registerKey,
      0,
    );
    if (index !== undefined && index > -1) {
      this.order.regions?.splice(index, 1);
    }
  }

  private removeRegisterFromRegion(register: string): void {
    const index = this.order.regions?.findIndex(
      (region) => region.label === register,
      0,
    );
    if (index !== undefined && index > -1) {
      this.order.regions?.splice(index, 1);
    }
  }
  private addRegisterToRegion(register: string): void {
    const index = this.order.regions?.findIndex(
      (region) => region.label === register,
      0,
    );
    if (index === -1 || index === undefined) {
      this.order.regions?.push({ label: register });
    }
  }

  onSearch = (search: string) => {
    this.filteredRegisters = this.registers.filter((register) =>
      register.toLowerCase().trim().includes(search.toLowerCase().trim()),
    );

    if (this.search.length === 0) {
      this.allSelected = this.registers.every((item) =>
        this.selectedOptions.has(item),
      );
    }
  };

  isSelected(register: string): boolean {
    return this.selectedOptions.has(register);
  }

  private changeOrderModel = (register: string) => {
    const count = this.getCount(this.registerKey);
    const selected = this.getSelected(this.registerKey);

    if (count <= LIMIT_OF_INDIVIDUAL_REGIONS) {
      selected.forEach((r) => {
        this.addRegisterToRegion(r);
      });
    }

    if (this.action === REMOVE) {
      if (count < LIMIT_OF_INDIVIDUAL_REGIONS) {
        this.removeRegisterFromRegion(register);
        this.removeRegion();
      } else if (count === LIMIT_OF_INDIVIDUAL_REGIONS) {
        this.removeRegion();
      }
    }
    if (this.action === ADD) {
      if (count <= LIMIT_OF_INDIVIDUAL_REGIONS) {
        this.removeRegion();
      } else {
        if (count === LIMIT_OF_INDIVIDUAL_REGIONS + 1) {
          selected.forEach((r) => {
            this.removeRegisterFromRegion(r);
          });
        }
        this.addRegion();
      }
    }
  };
  private changeOrderModelForNonRegionalProducts = (register: string) => {
    const index = this.order.regions?.findIndex(
      (region) => region.label === register,
      0,
    );
    if (index !== undefined && index > -1) {
      this.order.regions?.splice(index, 1);
    } else {
      this.order.regions?.push({ label: register });
    }
  };

  private modifyRegions(): void {
    this.order.regions
      ?.filter((region) => this.region.includes(region.label!))
      .forEach((region) => {
        region.limited =
          this.watchRegisters[region.label!].length !==
          this.getCount(region.label!)
            ? true
            : false;
      });
    this.mergeRegions();
  }

  private mergeRegions(): void {
    let allRegions = this.splitRegionsAndRegisters();

    if (
      allRegions.individualRegisters.length > LIMIT_OF_INDIVIDUAL_REGIONS ||
      this.isAllRegionsSelected()
    ) {
      const limited = this.isLimited();
      this.order.regions = [
        {
          label: WORLDWIDE,
          limited,
        } as Region,
      ];
      if (limited) {
        this.addAddedAndRemovedRegistersToOrderModel();
      }
    }

    allRegions = this.splitRegionsAndRegisters();

    if (
      allRegions.limitedRegions.length > 1 ||
      allRegions.fullRegions.some((r) => r.label === WORLDWIDE) ||
      allRegions.limitedRegions.some((r) => r.label === WORLDWIDE)
    ) {
      const limited = this.isLimited();
      this.order.regions = [
        {
          label: WORLDWIDE,
          limited,
        } as Region,
        ...allRegions.individualRegisters,
      ];
      if (limited) {
        this.addAddedAndRemovedRegistersToOrderModel();
      }
    }
  }

  private isLimited(): boolean {
    const val = this.order.regions?.find(
      (r: any) => r.label === WORLDWIDE,
    )?.limited;
    return val ? val : false;
  }

  private isAllRegionsSelected(): boolean {
    const worldWideRegisters = this.watchRegisters[WORLDWIDE];
    return (
      worldWideRegisters.length === this.selectedOptions.size &&
      worldWideRegisters.every((r) => this.selectedOptions.has(r))
    );
  }

  private splitRegionsAndRegisters(): {
    limitedRegions: Region[];
    fullRegions: Region[];
    individualRegisters: any;
  } {
    const regions: Region[] = [];
    const lRegions: Region[] = [];
    const fRegions: Region[] = [];
    const registers: any = [];

    this.order.regions?.forEach((region) => {
      if (this.isRegion(region.label!)) {
        regions.push(region);
      } else {
        registers.push(region);
      }
    });
    regions.forEach((r) => {
      if (r.limited) {
        lRegions.push(r);
      } else {
        fRegions.push(r);
      }
    });
    return {
      limitedRegions: lRegions,
      fullRegions: fRegions,
      individualRegisters: registers,
    };
  }

  private isRegion(item: string): boolean {
    return this.region.includes(item);
  }
}
